import { apiService } from '../lib/api';
import { 
  AuthUser, 
  AuthSession, 
  LoginRequest, 
  LoginResponse, 
  SignupRequest, 
  SignupResponse, 
  RefreshTokenRequest,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  AuthResult 
} from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3013';

class AuthService {
  private storageKey = 'ace-auth-session';

  // Get stored session
  getStoredSession(): AuthSession | null {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return null;
      
      const session: AuthSession = JSON.parse(stored);
      
      // Check if token is expired (with 5 second buffer)
      if (session.expires_at && Date.now() >= (session.expires_at * 1000 - 5000)) {
        this.clearStoredSession();
        return null;
      }
      
      // Validate session structure
      if (!session.access_token || !session.user?.id) {
        this.clearStoredSession();
        return null;
      }
      
      return session;
    } catch (error) {
      console.error('Error getting stored session:', error);
      this.clearStoredSession();
      return null;
    }
  }

  // Store session
  storeSession(session: AuthSession): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(session));
    } catch (error) {
      console.error('Error storing session:', error);
    }
  }

  // Clear stored session
  clearStoredSession(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Error clearing stored session:', error);
    }
  }

  // Login with email and password
  async login(email: string, password: string): Promise<AuthResult<LoginResponse>> {
    try {
      const payload: LoginRequest = { email, password };
      const response = await apiService.post<LoginResponse>('/auth/login', payload);
      
      // Store the session
      this.storeSession(response);
      
      return {
        data: response,
        error: undefined
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: {
          message: error.message || 'Login failed',
          code: error.status?.toString()
        }
      };
    }
  }

  // Sign up with email and password
  async signup(
    email: string, 
    password: string, 
    metadata: {
      first_name: string;
      last_name: string;
      user_type: 'student' | 'teacher';
      class?: string;
    }
  ): Promise<AuthResult<SignupResponse>> {
    try {
      const payload: SignupRequest = {
        email,
        password,
        metadata
      };
      
      const response = await apiService.post<SignupResponse>('/auth/signup', payload);
      
      // Don't store the session during signup - user needs to login manually
      // this.storeSession(response);
      
      return {
        data: response,
        error: undefined
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: {
          message: error.message || 'Signup failed',
          code: error.status?.toString()
        }
      };
    }
  }

  // Logout
  async logout(): Promise<AuthResult<void>> {
    try {
      // Clear stored session
      this.clearStoredSession();
      
      return {
        data: undefined,
        error: undefined
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: {
          message: error.message || 'Logout failed'
        }
      };
    }
  }

  // Get current user from stored session
  getCurrentUser(): AuthUser | null {
    const session = this.getStoredSession();
    return session?.user || null;
  }

  // Get current profile from stored profile
  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  // Get access token
  getAccessToken(): string | null {
    const session = this.getStoredSession();
    return session?.access_token || null;
  }

  // Refresh access token using refresh token
  async refreshToken(): Promise<AuthResult<RefreshTokenResponse>> {
    try {
      const currentSession = this.getStoredSession();
      if (!currentSession?.refresh_token) {
        return {
          data: undefined,
          error: { message: 'No refresh token available' }
        };
      }

      const response = await apiService.get<RefreshTokenResponse>(
        '/auth/token/refresh',
        currentSession.refresh_token
      );
      // Store the new session
      this.storeSession(response);
      
      // Dispatch event to notify AuthProvider of token refresh
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('token-refreshed', {
          detail: { session: response }
        }));
      }
      
      return {
        data: response,
        error: undefined
      };
    } catch (error: any) {
      // If refresh fails, clear the session
      this.clearStoredSession();
      
      return {
        data: undefined,
        error: {
          message: error.message || 'Token refresh failed',
          code: error.status?.toString()
        }
      };
    }
  }

  // Check if token needs refresh (refresh 5 minutes before expiry)
  private shouldRefreshToken(): boolean {
    const session = this.getStoredSession();
    if (!session?.expires_at) return false;
    
    const now = Date.now();
    const expiryTime = session.expires_at * 1000;
    // Refresh 5 minutes before expiry for seamless user experience
    const refreshThreshold = 5 * 60 * 1000; // 5 minutes
    
    return (expiryTime - now) <= refreshThreshold;
  }

  // Get access token with automatic refresh
  async getValidAccessToken(): Promise<string | null> {
    try {
      // Check if we need to refresh the token
      if (this.shouldRefreshToken()) {
        const refreshResult = await this.refreshToken();
        
        if (refreshResult.error) {
          return null;
        }
        
        return refreshResult.data?.access_token || null;
      }
      
      // Return current token if it's still valid
      return this.getAccessToken();
    } catch (error) {
      return null;
    }
  }

  // Handle authentication failure (clear session and redirect)
  handleAuthFailure(): void {
    this.clearStoredSession();
    
    // Also clear the legacy auth storage
    localStorage.removeItem('ace-auth');
    
    // Redirect to login if not already there
    if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
      window.location.href = '/login';
    }
  }

  // Handle 401 errors by attempting token refresh
  async handleUnauthorizedError(): Promise<boolean> {
    try {
      const refreshResult = await this.refreshToken();
      
      if (refreshResult.data && !refreshResult.error) {
        return true;
      } else {
        this.handleAuthFailure();
        return false;
      }
    } catch (error) {
      this.handleAuthFailure();
      return false;
    }
  }

  // Google OAuth login/signup
  async googleAuth(redirectUrl: string): Promise<AuthResult<{ auth_url: string; message: string }>> {
    try {
      const response = await apiService.get<{ auth_url: string; message: string }>('/auth/google/login');
      
      return {
        data: response,
        error: undefined
      };
    } catch (error: any) {
      // Provide more specific error messages
      let errorMessage = 'Google OAuth failed';
      
      if (error.status === 404) {
        errorMessage = 'Google OAuth endpoint not found. Please ensure your backend server is running and the endpoint is implemented.';
      } else if (error.status === 500) {
        errorMessage = 'Server error during Google OAuth. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        data: undefined,
        error: {
          message: errorMessage,
          code: error.status?.toString()
        }
      };
    }
  }

  // Forgot password
  async forgotPassword(email: string, redirectTo?: string): Promise<AuthResult<ForgotPasswordResponse>> {
    try {
      const payload: ForgotPasswordRequest = {
        email,
        redirect_to: redirectTo || `${window.location.origin}/reset-password`
      };
      
      const response = await apiService.post<ForgotPasswordResponse>('/auth/password/forgot', payload);
      
      return {
        data: response,
        error: undefined
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: {
          message: error.message || 'Failed to send password reset email',
          code: error.status?.toString()
        }
      };
    }
  }

  // Reset password
  async resetPassword(password: string, token: string): Promise<AuthResult<ResetPasswordResponse>> {
    try {
      const payload: ResetPasswordRequest = {
        password,
        token
      };
      
      const response = await apiService.post<ResetPasswordResponse>('/auth/password/reset', payload);
      
      // Store the new session after successful password reset
      this.storeSession(response);
      
      return {
        data: response,
        error: undefined
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: {
          message: error.message || 'Failed to reset password',
          code: error.status?.toString()
        }
      };
    }
  }
}

export const authService = new AuthService();
export default authService;
